import { get, omit, toPath, without } from 'lodash';
import { Query } from 'redux-little-router';

import { Action } from '../actions';
import {
    ADD_NEW_INTERNAL_BANNERS,
    AddNewInternalBannersAction,
    CLEAR_INTERNAL_BANNERS,
    ClearInternalBanners,
    COPY_INTERNAL_BANNER,
    CopyInternalBannerAction,
    DELETE_INTERNAL_BANNERS,
    DeleteInternalBanners,
    EDIT_INTERNAL_BANNER,
    GET_INTERNAL_BANNERS_FOR_EDIT_OR_CREATION,
    RESTORE_INTERNAL_BANNERS,
    RestoreInternalBanners,
    SAVE_INTERNAL_BANNERS,
    SAVE_INTERNAL_BANNERS_SUCCESS,
    SaveInternalBanners,
    SaveInternalBannersSuccess,
    UPDATE_INTERNAL_BANNER_BASE_INFO,
    UPDATE_INTERNAL_BANNER_IMAGE,
    UPDATE_INTERNAL_BANNER_TEMPLATE_RESOURCES,
    UPDATE_INTERNAL_BANNERS_FOR_EDIT,
    UpdateInternalBannerBaseInfo
} from '../actions/internalBannersEdit';
import { selectEditPageItemNameEditorValue } from '../selectors/editPageItemNameEditor/editPageItemNameEditor';
import { selectInternalBannerResources } from '../selectors/internalBannerEdit/internalBannerEdit';
import { internalBannerFieldErrorMapper } from '../service/BannerEditErrors/BannerEditErrors';
import {
    imageUploadTextErrorMapper,
    InternalBannersErrorsMapper
} from '../service/BannerEditErrors/ErrorsTextMapper/ErrorsTextMapper';
import {
    generateNewBannerId,
    getBannerEditPageItemEditorId
} from '../service/BannerHelpers/BannerHelpers';
import { BannersIdsForBannersEditHelpers } from '../service/BannersIdsForBannersEditHelpers';
import {
    buildErrorTree,
    convertServerPathToClient,
    mapTextToErrors
} from '../service/ErrorsMapper/ErrorsMapper';
import { makeNewInternalEditedBanner } from '../service/InternalBannerHelpers/InternalBannerHelpers';
import {
    BannerId,
    BannersScreenUrlSearchQueryParams
} from '../typings/banners';
import {
    ErrorsEdit,
    PageStatus,
    ValidationErrorsData,
    ValidationResultItem
} from '../typings/common';
import { CurrencyCode } from '../typings/graphql-grid';
import {
    InternalResourceId,
    InternalTemplateId,
    InternalTemplateVars
} from '../typings/internalBanners';
import {
    CopyInternalBannersInputData,
    EditedInternalBanner,
    EditedInternalBannerCampaign,
    EditInternalBannersState,
    InternalDraftBanner,
    InternalGroupBanners
} from '../typings/internalBannersEditState';
import { State } from '../typings/state';

const fillStateWithInternalBannerErrors = (
    errors: ValidationResultItem[],
    ids: BannerId[],
    state: EditInternalBannersState
): EditInternalBannersState => {
    const clientValidationItems = convertServerPathToClient(
        errors,
        internalBannerFieldErrorMapper
    );
    const errorsClientWithText = mapTextToErrors(
        clientValidationItems,
        InternalBannersErrorsMapper
    );
    const { banners: bannersTree } = buildErrorTree(
        errorsClientWithText
    ) as Partial<ErrorsEdit>;

    if (!bannersTree) {
        return state;
    }

    return Object.keys(bannersTree).reduce((stateCopy, key) => {
        const bannerId = ids[key];
        const errorsTree = bannersTree && bannersTree[key];

        const banners = stateCopy.banners;

        return {
            ...stateCopy,
            banners: {
                ...banners,
                [bannerId]: {
                    ...banners[bannerId],
                    errors: errorsTree
                }
            }
        };
    }, state);
};

const updateInternalBaseBannerInfo = (
    state: EditInternalBannersState,
    action: UpdateInternalBannerBaseInfo
): EditInternalBannersState => {
    const { field, value } = action.changed;
    const banner = state.banners[action.bannerId];

    const changes = {} as Partial<{
        errors: ValidationErrorsData;
        templateVars: InternalTemplateVars[];
    }>;

    if (banner.errors) {
        changes.errors = {
            ...banner.errors,
            [field]: {
                errors: []
            }
        };
    }

    if (field === 'templateId') {
        if (changes.errors) {
            // меняем шаблон? сбрасываем ошибки templateVars!
            delete changes.errors.templateVars;
        }

        // и генерируем ресурсы
        changes.templateVars = generateNewTemplateVars(
            state,
            banner.campaign.id,
            value,
            banner.templateVars
        );
    }

    return {
        ...state,
        banners: {
            ...state.banners,
            [action.bannerId]: {
                ...banner,
                ...changes,
                [field]: value
            }
        }
    };
};

const saveInternalBanners = (
    state: EditInternalBannersState,
    action: SaveInternalBanners
): EditInternalBannersState => {
    return {
        ...state,
        errorsSummary: {},
        errors: [],
        pageStatus: PageStatus.SAVING
    };
};

const setInternalBanners = (
    state: EditInternalBannersState,
    action: SaveInternalBannersSuccess
): EditInternalBannersState => {
    const { updated, added, deleted } = action.result;

    if (updated.banners.length) {
        const banners = { ...state.banners };

        updated.banners.forEach(updatedBanner => {
            if (!updatedBanner) {
                return;
            }

            // TODO: https://st.yandex-team.ru/DIRECT-98219
            const id = String(updatedBanner.id);

            banners[id] = {
                ...state.banners[id],
                isSaved: true
            };
        });

        state = {
            ...state,
            banners
        };
    }

    if (added.banners.length) {
        const banners = { ...state.banners };
        const bannersDraft = { ...state.bannersDraft };
        let bannersIds = [...state.bannersIds];
        const groupBannersList = { ...state.groupBannersList };

        added.banners.forEach((addedBanner, index) => {
            if (!addedBanner) {
                return;
            }
            const oldId = added.ids[index];
            const bannerData = state.banners[oldId];
            // TODO: https://st.yandex-team.ru/DIRECT-98219
            const newId = String(addedBanner.id);
            const groupId = bannerData.group.id;
            const groupBannersIds = [...groupBannersList[groupId].bannersIds];

            banners[newId] = {
                ...bannerData,
                isNew: false,
                id: newId,
                isSaved: true
            };

            bannersDraft[newId] = {
                ...bannersDraft[oldId]
            };

            if (groupBannersIds.includes(oldId)) {
                groupBannersIds[groupBannersIds.indexOf(oldId)] = newId;
                groupBannersList[groupId].bannersIds = groupBannersIds;
            }

            delete banners[oldId];
            delete bannersDraft[oldId];

            bannersIds = [...bannersIds, newId];
        });

        state = {
            ...state,
            banners,
            bannersDraft,
            bannersIds
        };
    }

    if (deleted.ids.length) {
        const banners = { ...state.banners };
        deleted.ids.forEach(deletedBannerId => {
            if (!deletedBannerId) {
                return;
            }

            banners[deletedBannerId] = {
                ...state.banners[deletedBannerId],
                isEditAllow: false
            };
        });

        state = {
            ...state,
            banners
        };
    }

    if (updated.errors.length) {
        state = {
            ...fillStateWithInternalBannerErrors(
                updated.errors,
                updated.ids,
                state
            )
        };
    }

    if (added.errors.length) {
        state = {
            ...fillStateWithInternalBannerErrors(added.errors, added.ids, state)
        };
    }

    state = {
        ...state,
        pageStatus: PageStatus.LOADED
    };

    return state;
};

const generateNewTemplateVars = (
    state: EditInternalBannersState,
    campaignId: string,
    templateId: InternalTemplateId,
    templateVars?: InternalTemplateVars[]
): InternalTemplateVars[] => {
    const list = new Map<string, InternalTemplateVars>();
    const resources = selectInternalBannerResources(
        state,
        campaignId,
        templateId
    );

    if (templateVars) {
        templateVars.forEach(value => list.set(value.resourceId, value));
    }

    if (resources) {
        resources.forEach(value => {
            if (!list.has(value.id)) {
                list.set(value.id, {
                    value: null,
                    templateId: value.templateId,
                    resourceId: value.id
                });
            }
        });
    }

    return Array.from(list.values());
};

const updateInternalBannerTemplateResources = (
    state: EditInternalBannersState,
    bannerId: BannerId,
    changed: {
        resourceId: InternalResourceId;
        value: string;
    }
): EditInternalBannersState => {
    const { errors, templateId, templateVars = [] } = state.banners[bannerId];

    const changes = {} as ValidationErrorsData;

    let resourceIndex = -1;

    const newTemplateVars = templateVars.map(templateVar => {
        const newTemplateVar = { ...templateVar };

        if (templateId === templateVar.templateId) {
            resourceIndex++;
        }

        if (templateVar.resourceId === changed.resourceId) {
            if (errors && errors.templateVars) {
                changes.templateVars = omit(errors.templateVars, [
                    resourceIndex
                ]);
            }

            newTemplateVar.value = changed.value;
        }

        return newTemplateVar;
    });

    if (errors && errors.images && errors.images.errors) {
        changes.images = {
            errors: errors.images.errors.filter(error => {
                const path = toPath(error.path);

                return path[path.length - 1] !== changed.resourceId;
            })
        };
    }

    return {
        ...state,
        banners: {
            ...state.banners,
            [bannerId]: {
                ...state.banners[bannerId],
                templateVars: newTemplateVars,
                errors: {
                    ...errors,
                    ...changes
                }
            }
        }
    };
};

const duplicateInternalBanners = (
    state: EditInternalBannersState,
    bannersList: CopyInternalBannersInputData[]
): EditInternalBannersState => {
    const banners: Record<BannerId, EditedInternalBanner> = {};
    const groupBannersLists: Record<string, InternalGroupBanners> = {};
    const bannersDrafts: Record<BannerId, InternalDraftBanner> = {};

    bannersList.forEach(({ id, groupId, bannerData, bannersDraft }) => {
        const group = state.groupBannersList[groupId];
        const bannerId =
            id || generateNewBannerId(group.campaign.id, group.groupId);

        banners[bannerId] = makeNewInternalEditedBanner(
            bannerId,
            true,
            {
                id: group.groupId,
                name: group.groupName,
                status: group.status,
                canEdit: group.canEdit,
                type: group.type
            },
            group.campaign as EditedInternalBannerCampaign,
            bannerData
        );

        bannersDrafts[bannerId] = bannersDraft || {
            ...state.bannersDraft[bannerId]
        };

        groupBannersLists[groupId] = {
            ...group,
            bannersIds: [...group.bannersIds, bannerId],
            bannersTotal: group.bannersTotal + 1
        };
    });

    return {
        ...state,
        banners: {
            ...state.banners,
            ...banners
        },
        bannersDraft: {
            ...state.bannersDraft,
            ...bannersDrafts
        },
        groupBannersList: {
            ...state.groupBannersList,
            ...groupBannersLists
        }
    };
};

const updateInternalBannerTemplateResourcesImagesErrors = (
    state: EditInternalBannersState,
    bannerId: BannerId,
    resourceId: InternalResourceId,
    errors: ValidationResultItem[]
): EditInternalBannersState => {
    const errorsClientWithText = mapTextToErrors(
        errors.map(serverError => ({
            ...serverError,
            path: `${bannerId}.images.${resourceId}`
        })),
        imageUploadTextErrorMapper
    );

    const oldErrors = get(
        state,
        `banners[${bannerId}].errors.images.errors`,
        []
    );

    const templateImagesErrors = oldErrors.filter(
        (error: ValidationResultItem) => {
            const path = toPath(error.path);
            return path[path.length - 1] !== resourceId;
        }
    );

    return {
        ...state,
        banners: {
            ...state.banners,
            [bannerId]: {
                ...state.banners[bannerId],
                errors: {
                    ...state.banners[bannerId].errors,
                    images: {
                        errors: [
                            ...errorsClientWithText,
                            ...templateImagesErrors
                        ]
                    }
                }
            }
        }
    };
};

export function addNewInternalBanners(
    state: EditInternalBannersState,
    action: AddNewInternalBannersAction
): EditInternalBannersState {
    return duplicateInternalBanners(state, action.banners);
}

export function copyInternalBannerReducer(
    state: EditInternalBannersState,
    action: CopyInternalBannerAction,
    fullState: State
): EditInternalBannersState {
    const banner = state.banners[action.bannerId];
    const bannersDraft = state.bannersDraft[action.bannerId];

    if (!banner && !bannersDraft) {
        throw new Error(
            `cannot find banner for duplicate id:${action.bannerId}`
        );
    }

    const { editPageItemNameEditor: editors } = fullState;

    const editorData = selectEditPageItemNameEditorValue(
        editors,
        getBannerEditPageItemEditorId(action.bannerId)
    );

    let description = banner.description;

    if (editorData.isVisible) {
        description = editorData.value;
    }

    return duplicateInternalBanners(state, [
        {
            id: action.newBannerId,
            groupId: banner.group.id,
            campaignId: banner.campaign.id,
            bannerType: banner.bannerType,
            bannersDraft: { ...bannersDraft },
            bannerData: {
                description: description,
                templateId: banner.templateId,
                templateVars: banner.templateVars.reduce<
                    InternalTemplateVars[]
                >((templateVars, templateVar) => {
                    if (templateVar.templateId === banner.templateId) {
                        templateVars.push({
                            templateId: templateVar.templateId,
                            resourceId: templateVar.resourceId,
                            value: templateVar.value
                        });
                    }

                    return templateVars;
                }, [])
            }
        }
    ]);
}

export interface EditInternalBannerPageLocalAndUrlData {
    campaignsIds: string[];
    bannersIds: string[];
    groupsIds: string[];
    isNew: boolean;
}

export const getEditInternalBannerPageData = (
    queryParams: Query
): EditInternalBannerPageLocalAndUrlData => {
    const editBannersLocalData = BannersIdsForBannersEditHelpers.getBannersEditSessionValue(
        queryParams.ulogin
    );

    const editBannersDataFromUrl = {
        campaignsIds: queryParams[
            BannersScreenUrlSearchQueryParams.campaignsIds
        ]
            ? queryParams[BannersScreenUrlSearchQueryParams.campaignsIds].split(
                  ','
              )
            : [],
        bannersIds: queryParams[BannersScreenUrlSearchQueryParams.bannersIds]
            ? queryParams[BannersScreenUrlSearchQueryParams.bannersIds].split(
                  ','
              )
            : [],
        groupsIds: queryParams[BannersScreenUrlSearchQueryParams.groupsIds]
            ? queryParams[BannersScreenUrlSearchQueryParams.groupsIds].split(
                  ','
              )
            : [],
        isNew: queryParams[BannersScreenUrlSearchQueryParams.isNew]
            ? Boolean(+queryParams[BannersScreenUrlSearchQueryParams.isNew])
            : false
    };

    const hasNotEmptyBannersDataFromUrl =
        editBannersDataFromUrl.campaignsIds.length !== 0 ||
        editBannersDataFromUrl.groupsIds.length !== 0 ||
        editBannersDataFromUrl.bannersIds.length !== 0;

    return hasNotEmptyBannersDataFromUrl
        ? editBannersDataFromUrl
        : editBannersLocalData;
};

export const EditInternalBannersInitialState: EditInternalBannersState = {
    banners: {},
    bannersDraft: {},
    bannersIds: [],
    campaignsIds: [],
    groupsIds: [],
    isNew: false,
    pageStatus: PageStatus.LOADING,
    groupBannersList: {},
    clientInfo: {
        isBannerUpdateAllowed: false,
        isCpmBannerUpdateAllowed: false,
        isBannerPricesOnGridEnabled: false,
        isTurboForSerpWizardAllowed: false,
        isTycoonOrganizationsEnabled: false,
        clientId: '',
        workCurrency: CurrencyCode.RUB
    },
    errors: [],
    placeTemplates: {},
    templateResources: {}
};

export function internalBannersMultiEdit(
    state: EditInternalBannersState = EditInternalBannersInitialState,
    action: Action,
    fullState: State
): EditInternalBannersState {
    switch (action.type) {
        case GET_INTERNAL_BANNERS_FOR_EDIT_OR_CREATION:
            return {
                ...state,
                pageStatus: PageStatus.LOADING,
                errorsSummary: undefined,
                errors: []
            };
        case DELETE_INTERNAL_BANNERS:
            return deleteInternalBanners(state, action);

        case CLEAR_INTERNAL_BANNERS:
            return clearInternalBanners(state, action);

        case RESTORE_INTERNAL_BANNERS:
            return restoreInternalBannersReducer(state, action);

        case COPY_INTERNAL_BANNER:
            return copyInternalBannerReducer(state, action, fullState);

        case UPDATE_INTERNAL_BANNERS_FOR_EDIT:
            const banners = action.data.banners || {};

            return {
                ...state,
                clientId: action.data.clientId,
                banners: {
                    ...banners
                },
                groupBannersList: {
                    ...action.data.groupBannersList
                },
                placeTemplates: action.placeTemplates,
                templateResources: action.templateResources,
                pageStatus: PageStatus.LOADED
            };
        case UPDATE_INTERNAL_BANNER_BASE_INFO:
            return updateInternalBaseBannerInfo(state, action);
        case UPDATE_INTERNAL_BANNER_TEMPLATE_RESOURCES:
            return updateInternalBannerTemplateResources(
                state,
                action.bannerId,
                action.changed
            );
        case UPDATE_INTERNAL_BANNER_IMAGE:
            return action.imageErrors && action.imageErrors.length
                ? updateInternalBannerTemplateResourcesImagesErrors(
                      state,
                      action.bannerId,
                      action.resourceId,
                      action.imageErrors
                  )
                : updateInternalBannerTemplateResources(
                      state,
                      action.bannerId,
                      {
                          resourceId: action.resourceId,
                          value: action.hash
                      }
                  );
        case EDIT_INTERNAL_BANNER:
            return {
                ...state,
                banners: {
                    ...state.banners,
                    [action.bannerId]: {
                        ...state.banners[action.bannerId],
                        isSaved: false
                    }
                }
            };
        case ADD_NEW_INTERNAL_BANNERS:
            return addNewInternalBanners(state, action);
        case SAVE_INTERNAL_BANNERS:
            return saveInternalBanners(state, action);
        case SAVE_INTERNAL_BANNERS_SUCCESS:
            return setInternalBanners(state, action);
        default:
            return state;
    }
}

export function deleteInternalBanners(
    state: EditInternalBannersState,
    action: DeleteInternalBanners
): EditInternalBannersState {
    const banners = { ...state.banners };
    const bannersDraft = { ...state.bannersDraft };

    // сначала удаляем из groupBannersList id-шники баннеров
    const groupBannersList = action.bannersIds.reduce(
        (result: Record<string, InternalGroupBanners>, bannerId: BannerId) => {
            const groupId = state.banners[bannerId].group.id;

            result[groupId] = {
                ...result[groupId],
                bannersIds: banners[bannerId].isNew
                    ? without(result[groupId].bannersIds, bannerId)
                    : result[groupId].bannersIds,
                bannersTotal: result[groupId].bannersTotal - 1
            };

            return result;
        },
        { ...state.groupBannersList }
    );

    // потом удаляем баннеры или помечаем их для удаления
    action.bannersIds.forEach((bannerId: BannerId) => {
        if (banners[bannerId].isNew) {
            delete banners[bannerId];
            delete bannersDraft[bannerId]; // удаляем bannersDraft только для новых объявлений, текущие можно будет восстановить
        } else {
            banners[bannerId].markedForDeletion = true;
        }
    });

    return {
        ...state,
        banners,
        bannersDraft,
        groupBannersList
    };
}

export function clearInternalBanners(
    state: EditInternalBannersState,
    action: ClearInternalBanners
): EditInternalBannersState {
    const banners = { ...state.banners };
    const bannersDraft = { ...state.bannersDraft };

    action.bannersIds.forEach(bannerId => {
        const banner = banners[bannerId];
        const groupId = banner.group.id;
        const group = state.groupBannersList[groupId];

        banners[bannerId] = makeNewInternalEditedBanner(
            banner.id,
            banner.isNew,
            {
                id: group.groupId,
                name: group.groupName,
                status: group.status,
                canEdit: group.canEdit,
                type: group.type
            },
            group.campaign,

            banner.isNew
                ? {
                      canBeDeleted: banner.canBeDeleted
                  }
                : {
                      // Менять идентификатор сохранённого шаблона нельзя, поэтому его не сбрасываем
                      templateId: banner.templateId,
                      // Генерируем пустые templateVars для неблокировки редактирования полей
                      templateVars: generateNewTemplateVars(
                          state,
                          banner.campaign.id,
                          banner.templateId
                      ),
                      canBeDeleted: banner.canBeDeleted
                  }
        );

        bannersDraft[bannerId] = {} as InternalDraftBanner;
    });

    return {
        ...state,
        banners,
        bannersDraft
    };
}

export function restoreInternalBannersReducer(
    state: EditInternalBannersState,
    action: RestoreInternalBanners
): EditInternalBannersState {
    const banners = { ...state.banners };

    action.bannersIds.forEach((bannerId: BannerId) => {
        banners[bannerId].markedForDeletion = false;
    });

    return {
        ...state,
        banners,

        groupBannersList: action.bannersIds.reduce(
            (
                result: Record<string, InternalGroupBanners>,
                bannerId: BannerId
            ) => {
                const groupId = banners[bannerId].group.id;

                result[groupId] = {
                    ...result[groupId],
                    bannersTotal: result[groupId].bannersTotal + 1
                };

                return result;
            },
            {
                ...state.groupBannersList
            }
        )
    };
}
